/**
 * Vault — local-first persistence for signed-in users.
 *
 * A minimal promise wrapper around IndexedDB with a single key-value object
 * store. Data lives only in the user's browser (nothing is sent to the
 * server); records are namespaced per user id so accounts on a shared device
 * don't collide. A future paid "sync across devices" tier can mirror these
 * records server-side without changing callers.
 *
 * All functions are safe to call during SSR and in private-browsing modes
 * where IndexedDB is unavailable: reads resolve to `undefined`, writes no-op.
 */

const DB_NAME = "toolverse-vault";
const DB_VERSION = 1;
const STORE = "kv";

let dbPromise: Promise<IDBDatabase> | null = null;

function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) {
          request.result.createObjectStore(STORE);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(request.error ?? new Error("vault: failed to open IndexedDB"));
      request.onblocked = () =>
        reject(new Error("vault: IndexedDB open blocked"));
    });
    // Allow a retry on the next call if opening failed (e.g. private mode).
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("vault: IndexedDB request failed"));
  });
}

/** Build the storage key for a user-scoped record. */
export function vaultKey(
  userId: string,
  namespace: string,
  key: string,
): string {
  return `${userId}:${namespace}:${key}`;
}

/** Read a record. Resolves to `undefined` when missing or storage is unavailable. */
export async function vaultGet<T>(key: string): Promise<T | undefined> {
  if (!isIndexedDbAvailable()) return undefined;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, "readonly");
    const result = await requestToPromise(tx.objectStore(STORE).get(key));
    return result as T | undefined;
  } catch {
    return undefined;
  }
}

/** Write a record. Silently no-ops when storage is unavailable. */
export async function vaultSet(key: string, value: unknown): Promise<void> {
  if (!isIndexedDbAvailable()) return;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    await requestToPromise(tx.objectStore(STORE).put(value, key));
  } catch {
    // Private mode / quota errors: local save is best-effort by design.
  }
}

/** Delete a record. Silently no-ops when storage is unavailable. */
export async function vaultDelete(key: string): Promise<void> {
  if (!isIndexedDbAvailable()) return;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    await requestToPromise(tx.objectStore(STORE).delete(key));
  } catch {
    // Best-effort, same as vaultSet.
  }
}
